import {styled} from '@mui/material';
import {SxProps, Theme} from '@mui/material/styles';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React from "react";
import CCOContainer from "../../fundamentals/CCOContainer";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import PreviewElementBoxDrawer from "../../preview/PreviewElementBoxDrawer";
import {metaDataProperty} from "../../preview/previewMetaData";
import {usePreviewContext} from "../../../context/PreviewContext";
import useWindowSize from "../../../utility/useWindowSize";
import CCOResponsivePicture from "../../fundamentals/CCOResponsivePicture";
import {C014StageCarouselPictureSettings} from "../../../common/constants/pictureSettings";
import {MARGINS} from "../../fundamentals/CCORichText/CCORichTextStyles";
import TeaserCardCallToAction, {
    CallToActionType
} from "../C020TeaserCarousel/TeaserCardCallToAction";
import {getAltText} from "../../fundamentals/CCOPicture";
import {useTeaserCardSelect} from 'tracking/hooks/useTeaserCardSelect';
import CCOAiGeneratedImageLabel, {useAiGeneratedImageLabel} from "../../fundamentals/CCOAiGeneratedImageLabel";

interface Props {
    item?: any
    hero?: boolean
    dataCypress?: string
    showAiGeneratedImageLabel?: boolean
}

const headlineStyle = (px: number) => ({
    background: 'white',
    px: px,
    py: 2,
});

const titleBoxStyle = {
    height: 'auto',
    marginTop: '16px'
}

const titleStyle = (padding: string, isWindowExtraSmallOrSmall: boolean, px: number) => ({
    '& span': {
        background: 'white',
        my: 0,
        py: padding,
        px: px,
        boxDecorationBreak: 'clone',
        lineHeight: isWindowExtraSmallOrSmall ? '2.1rem' : '2.7rem',
    },
    m: 0,
    p: 0
});

const textBoxStyle = (marginTop: number, marginBottom: string, padding: number) => ({
    background: 'white',
    p: padding,
    marginTop: marginTop,
    width: 'max-content',
    maxWidth: '100%',
    'div:last-child': {
        marginBottom: marginBottom,
    },
});

const placeholderStyle = (hero?: boolean): SxProps<Theme> => (theme) => ({
    height: hero ? '658px' : '540px',
    [theme.breakpoints.down('xl')]: {
        height: '514px'
    },
    [theme.breakpoints.down('lg')]: {
        height: '386px'
    },
    [theme.breakpoints.down('md')]: {
        height: '538px'
    },
    [theme.breakpoints.down('sm')]: {
        height: '530px'
    },
});

const heroImageStyle = (hero?: boolean): SxProps<Theme> => (theme) => ({
    position: "relative",
    height: hero ? '658px' : '540px',
    [theme.breakpoints.down('xl')]: {
        height: '514px'
    },
    [theme.breakpoints.down('lg')]: {
        height: '386px'
    },
    [theme.breakpoints.down('md')]: {
        height: '338px'
    },
    [theme.breakpoints.down('sm')]: {
        height: '330px'
    },
    display: "flex",
    alignItems: hero ? "center" : "end",
    justifyContent: "center",
    overflow: "hidden",
    textAlign: "left",
});

const textContentStyle = (hero?: boolean): SxProps<Theme> => (theme) => ({
    position: "relative",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "start",
    justifyContent: "start",
    flexDirection: "column",
    width: "100%",
    zIndex: 0, // Added a z-index so that this div stays above the Image component
    marginBottom: hero ? 0 : theme.spacing(10)
});

const ImageOverlay = styled("div")(() => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
}));

function C014StageTeaser(props: Props) {
    const isPreview = usePreviewContext().isPreview;
    const {
        isWindowExtraSmall,
        isWindowSmall,
        isWindowMedium,
        isWindowLarge,
        isWindowExtraLarge,
        lowerThanWindowMedium,
        lowerThanWindowSmall
    } = useWindowSize();

    const isWindowExtraSmallOrSmall = isWindowExtraSmall || isWindowSmall;
    const isWindowMediumOrLarger = isWindowMedium || isWindowLarge || isWindowExtraLarge;

    let headlinePadding = '5px';
    let boxMargin = 4;
    if (isWindowExtraLarge) {
        boxMargin = 6;
    }
    if (isWindowMedium) {
        headlinePadding = '8px';
        boxMargin = 4;
    } else if (isWindowLarge) {
        headlinePadding = '6px';
        boxMargin = 6;
    }

    const px = getTextContentPx(lowerThanWindowSmall, lowerThanWindowMedium);

    const hasContent = !!(
        (props.item?.headline && props.item.headline.trim() !== '') ||
        (props.item?.teaserTitle && props.item.teaserTitle.trim() !== '') ||
        (props.item?.teaserText?.textAsTree && props.item.teaserText.textAsTree.length > 0) ||
        hasTeaserTarget(props)
    );

    const handleTeaserSelect = useTeaserCardSelect(
        {item: props.item, teasercardLocation: 'C014 Stage-Teaser'});

    const teaserTextBox = renderTeaserTextBox(props, isPreview, px, headlinePadding,
                                              isWindowExtraSmallOrSmall, boxMargin,
                                              handleTeaserSelect);

    const picture = props.item?.picture ?? undefined
    const pictureAlt = getAltText(picture?.alt, picture?.title);
    const aiLabel = useAiGeneratedImageLabel(props.showAiGeneratedImageLabel ? picture : undefined, pictureAlt);

    const stageTeaserPositioning = picture?.mediaSettings?.positionForStageteaser;
    const stageTeaserPositionStyle = stageTeaserPositioning
        ? {
            height: "auto",
            minHeight: "100%",
            width: "100%",
            position: "absolute",
            top: stageTeaserPositioning === "top" ? "0" : undefined,
            bottom: stageTeaserPositioning === "bottom" ? "0" : undefined,
        }
        : {};

    const isDataReady = !!(
        props.item &&
        props.item.picture
    );


    if (!isDataReady) {
        return (
            <Box sx={placeholderStyle(props.hero)}/>
        );
    }

    return (
        <PreviewElementBoxDrawer propertyId={props.item?.id}>
            <div data-cypress={props.dataCypress}>
                <Box sx={heroImageStyle(props.hero)}>
                    {picture ?
                        <>
                            <CCOResponsivePicture dataCypress={"stageTeaserImage"}
                                                  pictureSetting={C014StageCarouselPictureSettings}
                                                  picture={picture}
                                                  hasPriority={!stageTeaserPositioning}
                                                  alt={aiLabel?.imageAlt ?? pictureAlt}
                                                  imageStyle={{
                                                      objectFit: "cover",
                                                      width: "100%",
                                                      height: "100%", ...stageTeaserPositionStyle
                                                  }}
                                                  pictureStyle={{
                                                      objectFit: "cover",
                                                      width: "100%",
                                                      height: "100%",
                                                      position: "absolute"
                                                  }}/>
                            <ImageOverlay/>
                            {aiLabel && <CCOAiGeneratedImageLabel label={aiLabel} variant='stage'/>}
                        </> : <></>
                    }
                    <Box sx={textContentStyle(props.hero)}>
                        <CCOContainer width={"full"}>
                            <Grid container>
                                {isWindowMediumOrLarger && hasContent ? teaserTextBox : null}
                            </Grid>
                        </CCOContainer>
                    </Box>
                </Box>
                {isWindowExtraSmallOrSmall && hasContent ?
                    <Box
                        sx={{
                            marginTop: 3,
                            marginBottom: 2,
                            padding: '4px'
                        }}>{teaserTextBox}</Box> :
                    null}
            </div>
        </PreviewElementBoxDrawer>
    );
}

function renderTeaserTextBox(props: Props, isPreview: boolean, px: number, headlinePadding: string,
                             isWindowExtraSmallOrSmall: boolean, boxMargin: number,
                             onClick?: any) {
    return <Grid
        size={{xs: 12, md: 6, lg: 6}}
        sx={{
            mx: 3,
            maxWidth: {xs: 'calc(100% - 24px)', md: 'none'},
        }}>
        {renderTeaserHeadline(props, isPreview, px)}
        {renderTeaserTitle(props, headlinePadding, isPreview, isWindowExtraSmallOrSmall, px)}
        {renderTeaserText(props, boxMargin, px, onClick)}
    </Grid>
}

function renderTeaserHeadline(props: Props, isPreview: boolean, px: number) {
    return !props.hero && props.item?.headline
           && <Typography variant='overline'
                          sx={headlineStyle(px)}
                          {...metaDataProperty(isPreview, 'properties.headline', props.item.id)}>
               {props.item?.headline}
           </Typography>
}

function renderTeaserTitle(props: Props, padding: string, isPreview: boolean,
                           isWindowExtraSmallOrSmall: boolean, px: number) {
    return props.item?.teaserTitle
           && <div style={titleBoxStyle}>
               <Typography variant='h3'
                           component={props.item?.teaserSettings?.activateH1 ? 'h1' : 'div'}
                           sx={titleStyle(padding, isWindowExtraSmallOrSmall, px)}>
              <span {...metaDataProperty(isPreview, 'properties.teaserTitle', props.item.id)}>
                {props.item?.teaserTitle}
              </span>
               </Typography>
           </div>
}

function renderTeaserText(props: Props, boxMargin: number, px: number, onClick?: any) {
    const teaserText = props.item?.teaserText;
    const hasTeaserButton = hasTeaserTarget(props);
    const marginBottom = hasTeaserButton ? MARGINS['p']?.bottom : '0';
    return (teaserText || hasTeaserButton)
           && <Box sx={textBoxStyle(boxMargin, marginBottom, px)}>
               {teaserText &&
                <CCORichText metadataId={props.item.id} propertyName='properties.teaserText'
                             textAsTree={teaserText.textAsTree}/>}
               {hasTeaserButton && <TeaserCardCallToAction item={props.item}
                                                           callToActionType={CallToActionType.BUTTON}
                                                           callToActionCustomText={props.item?.teaserCallToActionSettings?.callToActionCustomText}
                                                           onClick={onClick}
               />}
           </Box>;
}

function hasTeaserTarget(props: Props): boolean {
    return props.item?.teaserCallToActionSettings?.callToActionEnabled
           && props.item?.teaserTarget;
}

function getTextContentPx(lowerThanWindowSmall: boolean, lowerThanWindowMedium: boolean): number {
    if (lowerThanWindowSmall) {
        return 0;
    }
    if (lowerThanWindowMedium) {
        return 2;
    }
    return 4;
}

export default C014StageTeaser;